import { useMemo, useState } from 'react';

type RuleValues = {
    first: string;
    second: string;
    third: string;
    fourth: string;
};

const getMissingKey = (values: RuleValues) => {
    const entries = Object.entries(values) as Array<[keyof RuleValues, string]>;
    const emptyKeys = entries.filter(([, value]) => value.trim().length === 0).map(([key]) => key);
    if (emptyKeys.length !== 1) {
        return null;
    }

    return emptyKeys[0];
};

const parseNumber = (value: string) => {
    if (value.trim().length === 0) {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const RuleOfThreeTool = () => {
    const [values, setValues] = useState<RuleValues>({
        first: '',
        second: '',
        third: '',
        fourth: '',
    });

    const missingKey = useMemo(() => getMissingKey(values), [values]);

    const { result, errorMessage } = useMemo(() => {
        if (!missingKey) {
            return {
                result: '',
                errorMessage: 'Leave exactly one field empty to solve for it.',
            };
        }

        const parsedValues = {
            first: parseNumber(values.first),
            second: parseNumber(values.second),
            third: parseNumber(values.third),
            fourth: parseNumber(values.fourth),
        };

        const filledValues = Object.entries(parsedValues).filter(([key]) => key !== missingKey);
        if (filledValues.some(([, value]) => value === null)) {
            return {
                result: '',
                errorMessage: 'Enter valid numbers in the filled fields.',
            };
        }

        const firstValue = parsedValues.first ?? 0;
        const secondValue = parsedValues.second ?? 0;
        const thirdValue = parsedValues.third ?? 0;
        const fourthValue = parsedValues.fourth ?? 0;

        if (missingKey === 'first') {
            if (fourthValue === 0) {
                return { result: '', errorMessage: 'X cannot be 0.' };
            }
            return {
                result: ((secondValue * thirdValue) / fourthValue).toString(),
                errorMessage: '',
            };
        }

        if (missingKey === 'second') {
            if (thirdValue === 0) {
                return { result: '', errorMessage: 'C cannot be 0.' };
            }
            return {
                result: ((firstValue * fourthValue) / thirdValue).toString(),
                errorMessage: '',
            };
        }

        if (missingKey === 'third') {
            if (secondValue === 0) {
                return { result: '', errorMessage: 'B cannot be 0.' };
            }
            return {
                result: ((firstValue * fourthValue) / secondValue).toString(),
                errorMessage: '',
            };
        }

        if (firstValue === 0) {
            return { result: '', errorMessage: 'A cannot be 0.' };
        }

        return {
            result: ((secondValue * thirdValue) / firstValue).toString(),
            errorMessage: '',
        };
    }, [missingKey, values]);

    const handleChange =
        (key: keyof RuleValues) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setValues((current) => ({
                ...current,
                [key]: nextValue,
            }));
        };

    const handleClear = () => {
        setValues({
            first: '',
            second: '',
            third: '',
            fourth: '',
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Rule of Three</h2>
            <p className="text-sm text-gray-600 mb-4">Solve proportional values: A / B = C / X.</p>
            <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] items-center">
                <div className="flex flex-col gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rule-first">
                            A
                        </label>
                        <input
                            id="rule-first"
                            type="number"
                            inputMode="decimal"
                            className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            placeholder="A"
                            value={values.first}
                            onChange={handleChange('first')}
                        />
                    </div>
                    <div className="h-px bg-gray-200" />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rule-second">
                            B
                        </label>
                        <input
                            id="rule-second"
                            type="number"
                            inputMode="decimal"
                            className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            placeholder="B"
                            value={values.second}
                            onChange={handleChange('second')}
                        />
                    </div>
                </div>
                <div className="hidden md:flex items-center justify-center text-lg font-semibold text-gray-600">=</div>
                <div className="flex flex-col gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rule-third">
                            C
                        </label>
                        <input
                            id="rule-third"
                            type="number"
                            inputMode="decimal"
                            className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            placeholder="C"
                            value={values.third}
                            onChange={handleChange('third')}
                        />
                    </div>
                    <div className="h-px bg-gray-200" />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="rule-fourth">
                            X
                        </label>
                        <input
                            id="rule-fourth"
                            type="number"
                            inputMode="decimal"
                            className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            placeholder="X"
                            value={values.fourth}
                            onChange={handleChange('fourth')}
                        />
                    </div>
                </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={handleClear}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                    Clear
                </button>
                {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
            </div>
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Result</p>
                <p className="text-2xl font-semibold text-gray-900">{result || '—'}</p>
            </div>
        </div>
    );
};
